import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import * as Sentry from '@sentry/react';

// Redux
import { openModal, closeModal } from '../state/actions/ui/modal';
import { modalIds } from '../components/modals/SuperModal/types';

// GQL
import { client } from '../gql/client';
import { DELETE_MANY_IMAGES } from '../gql/mutations/accidents';

// Utils
import { showBackendErrorToast, showSuccessToast } from '../utils/toasts';
import i18n from '../i18n';

export interface DeletablePhoto {
  id: string;
  name: string;
}

/**
 * Borra una foto de un tramo, con confirmación.
 *
 * ─── Por qué con confirmación y no en dos toques ───────────────────────────
 *
 * Es irreversible y es EVIDENCIA de una intervención. En una tablet que se
 * maneja con guantes, un solo toque no puede llevarse una foto. Mismo gesto que
 * el resto de borrados de la app (`DeleteTeamModal`).
 *
 * ─── Por qué borra YA y no «al guardar» ────────────────────────────────────
 *
 * En Actuación las fotos se quitan dentro de un formulario que TIENE un guardar,
 * y el borrado se deduce comparando lo que hay contra lo que el usuario quiso.
 * La card del tramo no es un formulario: no hay «guardar» donde apoyarse, así
 * que la acción es explícita y se aplica en el momento.
 */
export function useDeleteSegmentPhoto(options: {
  /** Order del tramo — su `imageIDs` hay que sanearlo en la caché. */
  orderId?: string;
  /** Refresca la vista cuando el borrado ha ido bien. */
  onDeleted?: () => void;
}): { requestDelete: (photo: DeletablePhoto) => void } {
  const dispatch = useDispatch();
  const { orderId, onDeleted } = options;

  const requestDelete = useCallback(
    (photo: DeletablePhoto) => {
      dispatch(
        openModal(modalIds.DELETE_PHOTO_MODAL, {
          photoName: photo.name,
          onConfirmDelete: async () => {
            try {
              await client.mutate({
                mutation: DELETE_MANY_IMAGES,
                variables: { ids: [photo.id] },
                update(cache) {
                  // Sanear la Order en caché: si `imageIDs` conserva el id
                  // borrado, el hook que resuelve las imágenes lo volvería a
                  // pedir y la rejilla enseñaría un hueco hasta recargar.
                  if (!orderId) return;
                  cache.modify({
                    id: cache.identify({ __typename: 'Order', id: orderId }),
                    fields: {
                      imageIDs(current: unknown) {
                        const list = Array.isArray(current)
                          ? (current as string[])
                          : [];
                        return list.filter((id) => id !== photo.id);
                      },
                    },
                  });
                },
              });
              showSuccessToast(i18n.t('toasts:success.deleted'));
              onDeleted?.();
            } catch (error) {
              Sentry.captureException(error);
              // Mensaje del backend primero (doctrina de códigos estables): si
              // el servidor rechaza el borrado, el operario tiene que leer POR
              // QUÉ, no un «error general».
              showBackendErrorToast(error, i18n.t('errors:generalError'));
              throw error;
            } finally {
              dispatch(closeModal());
            }
          },
        })
      );
    },
    [dispatch, orderId, onDeleted]
  );

  return { requestDelete };
}

export default useDeleteSegmentPhoto;
